import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GiftIcon,
  UserGroupIcon,
  ChartBarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  TagIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import { getAppSocket } from '../lib/wesSocket';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';

interface ClientPoints {
  id: string;
  clientId: string;
  points: number;
  totalEarned: number;
  totalRedeemed: number;
  lastActivity: string;
}

interface Reward {
  id: string;
  name: string;
  description: string;
  pointsRequired: number;
  discountPercentage: number;
  discountAmount: number;
  rewardType: string;
  isActive: boolean;
}

interface Transaction {
  name: string;
  description: string;
  pointsUsed: number;
  discountApplied: number;
  redeemedAt: string;
  status: string;
}

interface CustomDiscount {
  id?: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount?: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category';
  target_id?: string;
  target_name?: string;
  active_days?: string[];
  active_hours?: string[];
  start_date: string;
  end_date: string;
  usage_type?: 'single' | 'multiple';
  reason?: 'fidelizacion' | 'reclamo' | 'promo' | 'cierre_venta' | 'error_interno' | '';
  eligible_products?: string[];
  eligible_services?: string[];
  eligible_clients?: string[];
  max_uses?: number;
  current_uses: number;
  enabled: boolean;
  priority: number;
  created_by_name?: string | null;
  last_applied_by_name?: string | null;
  last_applied_at?: string | null;
}

interface Coupon {
  id?: string;
  code: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount?: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category';
  target_id?: string;
  target_name?: string;
  start_date: string;
  end_date: string;
  max_uses?: number;
  current_uses: number;
  max_uses_per_client?: number;
  enabled: boolean;
  status?: 'active' | 'expired' | 'exhausted' | 'disabled';
  total_usage_count?: number;
  is_template?: number;
  integration_type?: string | null;
  integration_slug?: string | null;
  limit_mode?: string;
  max_total_codes?: number | null;
  ig_post_url?: string | null;
  expiration_days?: number | null;
  generated_codes_count?: number;
  used_codes_count?: number;
}

const actionButtonClass = 'size-8';
const actionSwitchContainerClass = 'flex h-8 items-center rounded-md border border-input bg-background px-2';
// unchecked visible también en modo oscuro
const actionSwitchClass = 'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:bg-gray-600';

const Loyalty: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [clientPoints, setClientPoints] = useState<ClientPoints | null>(null);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [showAddReward, setShowAddReward] = useState(false);
  const [showEditReward, setShowEditReward] = useState(false);
  const [showRedeem, setShowRedeem] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [loyaltyConfig, setLoyaltyConfig] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedRewards, setSelectedRewards] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Descuentos personalizados y cupones
  const [customDiscounts, setCustomDiscounts] = useState<CustomDiscount[]>([]);
  const [couponsData, setCouponsData] = useState<Coupon[]>([]);
  const [searchTermDiscounts, setSearchTermDiscounts] = useState('');
  const [searchTermCoupons, setSearchTermCoupons] = useState('');
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const realtimeTimerRef = useRef<number | null>(null);

  // Codigos generados por campania
  const [viewingCampaignCodes, setViewingCampaignCodes] = useState<any>(null);
  const [campaignCodes, setCampaignCodes] = useState<any[]>([]);
  const [campaignCodesPagination, setCampaignCodesPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Formulario para agregar premio
  const [rewardForm, setRewardForm] = useState({
    name: '',
    description: '',
    pointsRequired: 0,
    discountPercentage: 0,
    discountAmount: 0,
    rewardType: 'discount'
  });

  useEffect(() => {
    fetchClients();
    fetchRewards();
    fetchStats();
    fetchLoyaltyConfig();
    fetchCustomDiscounts();
    fetchCouponsData();
  }, []);

  // Socket real-time updates for discounts/coupons
  useEffect(() => {
    const socket = getAppSocket();
    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/custom-discounts') || path.startsWith('/api/coupons'))) return;
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchCustomDiscounts({ silent: true });
        fetchCouponsData({ silent: true });
      }, 450);
    };
    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);
    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchClientPoints(selectedClient);
      fetchTransactions(selectedClient);
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/clients');
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error al obtener clientes:', error);
    }
  };

  const fetchClientPoints = async (clientId: string) => {
    try {
      const response = await axios.get(`/api/loyalty/client/${clientId}`);
      setClientPoints(response.data.clientLoyalty?.points || null);
    } catch (error) {
      console.error('Error al obtener puntos:', error);
    }
  };

  const fetchRewards = async () => {
    try {
      const response = await axios.get('/api/loyalty/rewards');
      setRewards(response.data.rewards || []);
    } catch (error) {
      console.error('Error al obtener premios:', error);
    }
  };

  const fetchTransactions = async (clientId: string) => {
    try {
      const response = await axios.get(`/api/loyalty/client/${clientId}`);
      setTransactions(response.data.clientLoyalty?.redemptionHistory || []);
    } catch (error) {
      console.error('Error al obtener transacciones:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/loyalty/dashboard');
      setStats(response.data || null);
    } catch (error) {
      console.error('Error al obtener estadisticas:', error);
    }
  };

  const handleAddReward = async () => {
    try {
      await axios.post('/api/loyalty/rewards', rewardForm);
      toast.success('Premio agregado exitosamente');
      setShowAddReward(false);
      setRewardForm({ name: '', description: '', pointsRequired: 0, discountPercentage: 0, discountAmount: 0, rewardType: 'discount' });
      fetchRewards();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al agregar premio');
    }
  };

  const handleRedeemPoints = async () => {
    if (!selectedClient || !selectedReward) return;
    try {
      await axios.post('/api/loyalty/redeem-points', {
        clientId: selectedClient,
        rewardId: selectedReward.id
      });
      toast.success('Puntos canjeados exitosamente');
      setShowRedeem(false);
      setSelectedReward(null);
      fetchClientPoints(selectedClient);
      fetchTransactions(selectedClient);
      fetchStats();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al canjear puntos');
    }
  };

  const handleEditReward = async () => {
    if (!editingReward) return;
    try {
      await axios.put(`/api/loyalty/rewards/${editingReward.id}`, rewardForm);
      toast.success('Premio actualizado');
      setShowEditReward(false);
      setEditingReward(null);
      setRewardForm({ name: '', description: '', pointsRequired: 0, discountPercentage: 0, discountAmount: 0, rewardType: 'discount' });
      fetchRewards();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar premio');
    }
  };

  const handleDeleteReward = async (rewardId: string) => {
    if (!await confirmDialog('Eliminar este premio?')) return;
    try {
      await axios.delete(`/api/loyalty/rewards/${rewardId}`);
      toast.success('Premio eliminado');
      fetchRewards();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar premio');
    }
  };

  const handleBulkDeleteRewards = async () => {
    if (selectedRewards.length === 0) return;
    if (!await confirmDialog(`Eliminar ${selectedRewards.length} premio(s)?`)) return;
    try {
      await Promise.all(selectedRewards.map(id => axios.delete(`/api/loyalty/rewards/${id}`)));
      toast.success(`${selectedRewards.length} premio(s) eliminado(s)`);
      setSelectedRewards([]);
      setSelectAll(false);
      fetchRewards();
    } catch (error: any) {
      toast.error('Error al eliminar premios');
    }
  };

  const toggleRewardSelection = (rewardId: string) => {
    if (selectedRewards.includes(rewardId)) {
      setSelectedRewards(selectedRewards.filter(id => id !== rewardId));
    } else {
      setSelectedRewards([...selectedRewards, rewardId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedRewards([]);
    } else {
      setSelectedRewards(rewards.map(r => r.id));
    }
    setSelectAll(!selectAll);
  };

  const fetchLoyaltyConfig = async () => {
    try {
      const response = await axios.get('/api/loyalty/config');
      setLoyaltyConfig(response.data.config || {});
    } catch (error) {
      console.error('Error al obtener configuracion:', error);
    }
  };

  const handleUpdateConfig = async (configKey: string, configValue: string) => {
    try {
      await axios.put('/api/loyalty/config', { configKey, configValue });
      toast.success('Configuracion actualizada');
      fetchLoyaltyConfig();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar configuracion');
    }
  };

  // ─── Descuentos / Cupones Functions ──────────────────────────────

  const fetchCustomDiscounts = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoadingDiscounts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/custom-discounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomDiscounts(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      if (!silent) toast.error('Error cargando descuentos personalizados');
      setCustomDiscounts([]);
    } finally {
      if (!silent) setLoadingDiscounts(false);
    }
  };

  const deleteCustomDiscount = async (id: string) => {
    if (!await confirmDialog('Eliminar este descuento personalizado?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/custom-discounts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Descuento eliminado');
      fetchCustomDiscounts();
    } catch { toast.error('Error eliminando descuento'); }
  };

  const toggleCustomDiscount = async (id: string, enabled: boolean) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/custom-discounts/${id}`, { enabled }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Descuento ${enabled ? 'activado' : 'desactivado'}`);
      fetchCustomDiscounts();
    } catch { toast.error('Error actualizando descuento'); }
  };

  const fetchCouponsData = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoadingCoupons(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/coupons?limit=100', { headers: { Authorization: `Bearer ${token}` } });
      let data: Coupon[] = [];
      if (response.data?.coupons && Array.isArray(response.data.coupons)) data = response.data.coupons;
      else if (Array.isArray(response.data)) data = response.data;
      setCouponsData(data);
    } catch (error: any) {
      if (!silent) toast.error('Error cargando cupones');
      setCouponsData([]);
    } finally {
      if (!silent) setLoadingCoupons(false);
    }
  };

  const deleteCoupon = async (id: string) => {
    if (!await confirmDialog('Eliminar este cupon?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cupon eliminado');
      fetchCouponsData();
    } catch { toast.error('Error eliminando cupon'); }
  };

  const toggleCoupon = async (id: string, enabled: boolean) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/coupons/${id}`, { enabled }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Cupon ${enabled ? 'activado' : 'desactivado'}`);
      fetchCouponsData();
    } catch { toast.error('Error actualizando cupon'); }
  };

  const filteredDiscounts = useMemo(() => {
    if (!searchTermDiscounts.trim()) return customDiscounts;
    const s = searchTermDiscounts.toLowerCase();
    return customDiscounts.filter(d =>
      d.name.toLowerCase().includes(s) || (d.description || '').toLowerCase().includes(s) ||
      (d.reason || '').toLowerCase().includes(s) || d.discount_value.toString().includes(s)
    );
  }, [customDiscounts, searchTermDiscounts]);

  const filteredCoupons = useMemo(() => {
    if (!searchTermCoupons.trim()) return couponsData;
    const s = searchTermCoupons.toLowerCase();
    return couponsData.filter(c =>
      c.code.toLowerCase().includes(s) || c.name.toLowerCase().includes(s) ||
      c.description.toLowerCase().includes(s) || c.discount_value.toString().includes(s)
    );
  }, [couponsData, searchTermCoupons]);

  // Solo códigos individuales acá; las reglas maestras viven en /coupon-rules
  const generatedCoupons = useMemo(() => filteredCoupons.filter((c: any) => !c.is_template), [filteredCoupons]);

  const formatDiscountScope = (discount: CustomDiscount) => {
    if (discount.eligible_clients && discount.eligible_clients.length > 0) return 'Por cliente';
    if (discount.applies_to === 'products') return 'Por producto';
    if (discount.applies_to === 'services') return 'Por servicio';
    return 'General';
  };

  const fetchCampaignCodes = useCallback(async (couponId: number, page = 1) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/coupons/${couponId}/generated-codes?page=${page}&limit=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaignCodes(response.data.codes || []);
      setCampaignCodesPagination(response.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error al obtener codigos generados:', error);
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Fidelizacion</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gestiona puntos, premios, descuentos, cupones y codigos de bienvenida
          </p>
        </div>
        {activeTab === 'rewards' && (
          <div className="flex gap-2">
            {selectedRewards.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleBulkDeleteRewards}>
                <TrashIcon className="h-4 w-4 mr-1.5" />
                Eliminar ({selectedRewards.length})
              </Button>
            )}
            <Button size="sm" onClick={() => { setRewardForm({ name: '', description: '', pointsRequired: 0, discountPercentage: 0, discountAmount: 0, rewardType: 'discount' }); setShowAddReward(true); }}>
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Nuevo Premio
            </Button>
          </div>
        )}
        {activeTab === 'discounts' && (
          <Button size="sm" onClick={() => navigate('/discounts/new')}>
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Nuevo Descuento
          </Button>
        )}
        {activeTab === 'coupons' && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/coupon-rules')}>
              <TagIcon className="h-4 w-4 mr-1.5" />
              Reglas de cupones
            </Button>
            <Button size="sm" onClick={() => navigate('/discounts/coupons/new')}>
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Nuevo Cupon
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="dashboard">
            <ChartBarIcon className="h-4 w-4 mr-1.5" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="clients">
            <UserGroupIcon className="h-4 w-4 mr-1.5" />
            Clientes
          </TabsTrigger>
          <TabsTrigger value="rewards">
            <GiftIcon className="h-4 w-4 mr-1.5" />
            Premios
          </TabsTrigger>
          <TabsTrigger value="discounts">
            <TagIcon className="h-4 w-4 mr-1.5" />
            Descuentos
          </TabsTrigger>
          <TabsTrigger value="coupons">
            <TicketIcon className="h-4 w-4 mr-1.5" />
            Cupones
          </TabsTrigger>
          <TabsTrigger value="config">
            <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
            Config
          </TabsTrigger>
        </TabsList>

        {/* ─── Dashboard ─────────────────────────────────────────── */}
        <TabsContent value="dashboard" className="mt-4 space-y-6">
          {stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Clientes activos</p>
                  <p className="text-2xl font-bold mt-1">{stats.activeClients || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Puntos emitidos</p>
                  <p className="text-2xl font-bold mt-1">{stats.totalPointsIssued || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Puntos canjeados</p>
                  <p className="text-2xl font-bold mt-1">{stats.totalPointsRedeemed || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Premios disponibles</p>
                  <p className="text-2xl font-bold mt-1">{rewards.filter(r => r.isActive).length}</p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
        </TabsContent>

        {/* ─── Clientes ──────────────────────────────────────────── */}
        <TabsContent value="clients" className="mt-4 space-y-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Seleccionar cliente</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger>
                      <SelectValue placeholder="Elegir un cliente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.firstName} {client.lastName} {client.companyName ? `(${client.companyName})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {clientPoints && (
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 rounded-lg p-4 text-center flex-1">
                      <p className="text-xs text-muted-foreground">Puntos actuales</p>
                      <p className="text-2xl font-bold text-primary">{clientPoints.points || 0}</p>
                    </div>
                    <Button size="sm" onClick={() => setShowRedeem(true)} disabled={!clientPoints.points}>
                      <GiftIcon className="h-4 w-4 mr-1.5" />
                      Canjear
                    </Button>
                  </div>
                )}
              </div>

              {/* Historial de transacciones */}
              {selectedClient && transactions.length > 0 && (
                <div className="space-y-2">
                  <Label>Historial de canjes</Label>
                  <div className="relative w-full overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Premio</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Puntos</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Descuento</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((tx, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{tx.name}</TableCell>
                            <TableCell className="py-1 px-2 align-middle">{tx.pointsUsed}</TableCell>
                            <TableCell className="py-1 px-2 align-middle">${tx.discountApplied}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(tx.redeemedAt).toLocaleDateString('es-AR')}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <Badge className={tx.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800'}>
                                {tx.status === 'completed' ? 'Completado' : tx.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              {selectedClient && transactions.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Este cliente no tiene canjes registrados</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Premios ───────────────────────────────────────────── */}
        <TabsContent value="rewards" className="mt-4 space-y-4">
          <Card>
            <CardContent className="p-0">
              {rewards.length === 0 ? (
                <div className="text-center py-12">
                  <GiftIcon className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay premios configurados</p>
                  <Button size="sm" className="mt-4" onClick={() => setShowAddReward(true)}>
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    Crear primer premio
                  </Button>
                </div>
              ) : (
                <div className="relative w-full overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <input type="checkbox" checked={selectAll} onChange={toggleSelectAll} className="rounded" />
                        </TableHead>
                        <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                        <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Puntos</TableHead>
                        <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Descuento</TableHead>
                        <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                        <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                        <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rewards.map((reward) => (
                        <TableRow key={reward.id}>
                          <TableCell className="w-10">
                            <input
                              type="checkbox"
                              checked={selectedRewards.includes(reward.id)}
                              onChange={() => toggleRewardSelection(reward.id)}
                              className="rounded"
                            />
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle">
                            <div className="font-medium">{reward.name}</div>
                            {reward.description && <div className="text-xs text-muted-foreground">{reward.description}</div>}
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle">{reward.pointsRequired}</TableCell>
                          <TableCell className="py-1 px-2 align-middle">
                            {reward.discountPercentage > 0 ? `${reward.discountPercentage}%` : `$${reward.discountAmount}`}
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle">
                            <Badge variant="outline">{reward.rewardType === 'discount' ? 'Descuento' : reward.rewardType}</Badge>
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle">
                            <Badge className={reward.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}>
                              {reward.isActive ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                                setEditingReward(reward);
                                setRewardForm({
                                  name: reward.name,
                                  description: reward.description,
                                  pointsRequired: reward.pointsRequired,
                                  discountPercentage: reward.discountPercentage,
                                  discountAmount: reward.discountAmount,
                                  rewardType: reward.rewardType
                                });
                                setShowEditReward(true);
                              }}>
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteReward(reward.id)}>
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Descuentos Personalizados ────────────────────────── */}
        <TabsContent value="discounts" className="mt-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Buscar por nombre o descripcion..."
                value={searchTermDiscounts}
                onChange={(e) => setSearchTermDiscounts(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              {loadingDiscounts ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredDiscounts.length === 0 ? (
                <div className="text-center py-12">
                  <TagIcon className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchTermDiscounts ? 'No se encontraron descuentos' : 'No hay descuentos personalizados'}
                  </p>
                  {!searchTermDiscounts && (
                    <Button size="sm" className="mt-4" onClick={() => navigate('/discounts/new')}>
                      <PlusIcon className="h-4 w-4 mr-1.5" />
                      Crear primer descuento
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="hidden md:block relative w-full">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Alcance</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Condiciones</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Motivo</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Responsable</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDiscounts.map((discount) => (
                          <TableRow key={discount.id}>
                            <TableCell className="py-1 px-2 align-middle">
                              <div className="font-medium text-foreground">{discount.name}</div>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <div className="font-medium">{formatDiscountScope(discount)}</div>
                              <div className="text-xs text-muted-foreground">
                                {discount.discount_type === 'percentage' ? `${discount.discount_value}%` : `$${discount.discount_value}`}
                              </div>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <div className="text-sm text-muted-foreground">
                                {discount.usage_type === 'single' ? 'Uso unico' : 'Uso multiple'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Minimo: {discount.min_amount ? `$${discount.min_amount}` : 'Sin minimo'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Vigencia: {discount.start_date} - {discount.end_date}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Usos: {discount.current_uses}/{discount.max_uses || '\u221E'}
                              </div>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <div className="text-sm text-muted-foreground">
                                {discount.reason ? discount.reason.replace(/_/g, ' ') : '-'}
                              </div>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <div className="text-sm text-muted-foreground">
                                Creado: {discount.created_by_name || 'Sistema'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Aplicado: {discount.last_applied_by_name || 'Sin uso'}
                              </div>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <Badge variant={discount.enabled ? 'default' : 'secondary'}>
                                {discount.enabled ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end">
                                <ButtonGroup>
                                  <Tip label="Editar">
                                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/discounts/${discount.id}/edit`); }} className={actionButtonClass}>
                                      <PencilIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                  <Tip label={discount.enabled ? 'Desactivar' : 'Activar'}>
                                    <div className={actionSwitchContainerClass} onClick={(e) => e.stopPropagation()}>
                                      <Switch checked={!!discount.enabled} onCheckedChange={(checked) => toggleCustomDiscount(discount.id!, checked)} className={actionSwitchClass} />
                                    </div>
                                  </Tip>
                                  <Tip label="Eliminar">
                                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); deleteCustomDiscount(discount.id!); }} className={actionButtonClass}>
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                </ButtonGroup>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden divide-y">
                    {filteredDiscounts.map((discount) => (
                      <div key={discount.id} className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-foreground truncate">{discount.name}</div>
                            <div className="text-sm text-muted-foreground mt-0.5">
                              {formatDiscountScope(discount)} &middot; {discount.discount_type === 'percentage' ? `${discount.discount_value}%` : `$${discount.discount_value}`}
                            </div>
                          </div>
                          <Badge variant={discount.enabled ? 'default' : 'secondary'} className="shrink-0">
                            {discount.enabled ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>{discount.usage_type === 'single' ? 'Uso unico' : 'Uso multiple'}</span>
                          <span>Usos: {discount.current_uses}/{discount.max_uses || '\u221E'}</span>
                          <span>Min: {discount.min_amount ? `$${discount.min_amount}` : '-'}</span>
                          <span>Motivo: {discount.reason ? discount.reason.replace(/_/g, ' ') : '-'}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Vigencia: {discount.start_date} - {discount.end_date}</div>
                        <div className="flex items-center justify-end gap-2 pt-1">
                          <Button variant="outline" size="icon" onClick={() => navigate(`/discounts/${discount.id}/edit`)} className={actionButtonClass}>
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <div className={actionSwitchContainerClass}>
                            <Switch checked={!!discount.enabled} onCheckedChange={(checked) => toggleCustomDiscount(discount.id!, checked)} className={actionSwitchClass} />
                          </div>
                          <Button variant="outline" size="icon" onClick={() => deleteCustomDiscount(discount.id!)} className={actionButtonClass}>
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Cupones ─────────────────────────────────────────────── */}
        <TabsContent value="coupons" className="mt-4 space-y-4">

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Buscar por codigo, nombre o descripcion..."
                value={searchTermCoupons}
                onChange={(e) => setSearchTermCoupons(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              {loadingCoupons ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : generatedCoupons.length === 0 ? (
                <div className="text-center py-12">
                  <TicketIcon className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchTermCoupons ? 'No se encontraron cupones' : 'Todavía no hay cupones generados'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="hidden md:block relative w-full">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Codigo</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Descripcion</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Descuento</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Usos</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Válido hasta</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                          <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {generatedCoupons.map((coupon: any) => (
                          <TableRow key={coupon.id}>
                            <TableCell className="py-1 px-2 align-middle">
                              {coupon.is_template ? (
                                <Badge variant="outline" className="text-[10px] font-mono bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400 border-violet-200 dark:border-violet-800">PLANTILLA</Badge>
                              ) : (
                                <Badge variant="secondary" className="font-mono whitespace-nowrap">{coupon.code}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <div className="font-medium text-foreground truncate max-w-[240px]">{coupon.name}</div>
                              {Boolean(coupon.is_template) && coupon.integration_type && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Badge className={`text-[9px] px-1.5 py-0 ${coupon.integration_type === 'manychat_welcome' ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400' : 'bg-violet-100 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400'}`}>
                                    {coupon.integration_type === 'manychat_welcome' ? 'ManyChat Bienvenida' : 'ManyChat Campaña'}
                                  </Badge>
                                  {coupon.integration_slug && <code className="text-[9px] text-muted-foreground font-mono">{coupon.integration_slug}</code>}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              {coupon.is_template ? (
                                <div className="text-xs text-muted-foreground">
                                  <span className="text-violet-600 dark:text-violet-400 font-semibold">{coupon.generated_codes_count || 0}</span> gen · <span className="text-green-600 dark:text-green-400 font-semibold">{coupon.used_codes_count || 0}</span> usados
                                </div>
                              ) : (
                                <div className="cq-clamp-narrow text-sm text-muted-foreground truncate max-w-xs">{coupon.description || '-'}</div>
                              )}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <div className="font-medium">
                                {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}
                              </div>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              {coupon.is_template ? (
                                <div className="text-sm text-muted-foreground">{coupon.expiration_days || 30}d</div>
                              ) : (
                                <div className="text-sm text-muted-foreground">{coupon.current_uses}/{coupon.max_uses || '\u221E'}</div>
                              )}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              {coupon.is_template ? (
                                <div className="text-sm text-muted-foreground">-</div>
                              ) : (
                                <div className="text-sm text-muted-foreground">{coupon.end_date}</div>
                              )}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle">
                              <Badge variant={coupon.status === 'active' ? 'default' : coupon.status === 'expired' ? 'destructive' : 'secondary'}>
                                {coupon.status === 'active' ? 'Activo' : coupon.status === 'expired' ? 'Expirado' : coupon.status === 'exhausted' ? 'Agotado' : 'Desactivado'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end">
                                <ButtonGroup>
                                  {Boolean(coupon.is_template) && (coupon.generated_codes_count || 0) > 0 && (
                                    <Tip label="Ver codigos">
                                      <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); setViewingCampaignCodes(coupon); fetchCampaignCodes(coupon.id); }} className={actionButtonClass}>
                                        <TicketIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  )}
                                  <Tip label="Editar">
                                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/discounts/coupons/${coupon.id}/edit`); }} className={actionButtonClass}>
                                      <PencilIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                  <Tip label="Eliminar">
                                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); deleteCoupon(coupon.id!); }} className={actionButtonClass}>
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                </ButtonGroup>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile cards for coupons */}
                  <div className="md:hidden divide-y">
                    {generatedCoupons.map((coupon: any) => (
                      <div key={coupon.id} className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {coupon.is_template ? (
                              <Badge variant="outline" className="text-[10px] font-mono bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400 border-violet-200 dark:border-violet-800 mb-1">PLANTILLA</Badge>
                            ) : (
                              <Badge variant="secondary" className="font-mono text-xs mb-1">{coupon.code}</Badge>
                            )}
                            <div className="font-medium text-foreground truncate">{coupon.name}</div>
                            {Boolean(coupon.is_template) && coupon.integration_type && (
                              <Badge className={`text-[9px] px-1.5 py-0 mt-0.5 ${coupon.integration_type === 'manychat_welcome' ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400' : 'bg-violet-100 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400'}`}>
                                {coupon.integration_type === 'manychat_welcome' ? 'ManyChat Bienvenida' : 'ManyChat Campaña'}
                              </Badge>
                            )}
                            {!coupon.is_template && coupon.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{coupon.description}</div>}
                          </div>
                          <Badge variant={coupon.status === 'active' ? 'default' : coupon.status === 'expired' ? 'destructive' : 'secondary'} className="shrink-0">
                            {coupon.status === 'active' ? 'Activo' : coupon.status === 'expired' ? 'Expirado' : coupon.status === 'exhausted' ? 'Agotado' : 'Desactivado'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>Descuento: {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}</span>
                          {coupon.is_template ? (
                            <>
                              <span><span className="text-violet-600 dark:text-violet-400 font-semibold">{coupon.generated_codes_count || 0}</span> gen · <span className="text-green-600 dark:text-green-400 font-semibold">{coupon.used_codes_count || 0}</span> usados</span>
                              <span>Vence: {coupon.expiration_days || 30} dias</span>
                            </>
                          ) : (
                            <>
                              <span>Usos: {coupon.current_uses}/{coupon.max_uses || '\u221E'}</span>
                              <span>Válido hasta: {coupon.end_date}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-1">
                          {Boolean(coupon.is_template) && (coupon.generated_codes_count || 0) > 0 && (
                            <Tip label="Ver codigos">
                              <Button variant="outline" size="icon" onClick={() => { setViewingCampaignCodes(coupon); fetchCampaignCodes(coupon.id); }} className={actionButtonClass}>
                                <TicketIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          )}
                          <Button variant="outline" size="icon" onClick={() => navigate(`/discounts/coupons/${coupon.id}/edit`)} className={actionButtonClass}>
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="icon" onClick={() => deleteCoupon(coupon.id!)} className={actionButtonClass}>
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>


        </TabsContent>

        {/* ─── Configuracion ─────────────────────────────────────── */}
        <TabsContent value="config" className="mt-4 space-y-4">
          {/* Switch global del sistema de puntos */}
          {Boolean(loyaltyConfig?.loyalty_enabled) && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Sistema de puntos</h3>
                    <p className="text-sm text-muted-foreground">
                      {loyaltyConfig.loyalty_enabled.value === 'true'
                        ? 'Los puntos se asignan automaticamente al crear remitos'
                        : 'El sistema de puntos esta desactivado. No se asignan puntos automaticamente.'}
                    </p>
                  </div>
                  <Switch
                    checked={loyaltyConfig.loyalty_enabled.value === 'true'}
                    onCheckedChange={(checked) => {
                      const val = checked ? 'true' : 'false';
                      const newConfig = { ...loyaltyConfig };
                      newConfig.loyalty_enabled.value = val;
                      setLoyaltyConfig(newConfig);
                      handleUpdateConfig('loyalty_enabled', val);
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Configuracion numerica */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-medium mb-4">Configuracion del sistema de puntos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loyaltyConfig && Object.entries(loyaltyConfig)
                  .filter(([key]) => key !== 'loyalty_enabled')
                  .map(([key, config]: [string, any]) => (
                  <div key={key} className="border rounded-lg p-4 space-y-2">
                    <Label className="capitalize">{key.replace(/_/g, ' ')}</Label>
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                    <div className="flex gap-2">
                      <Input
                        value={config.value}
                        onChange={(e) => {
                          const newConfig = { ...loyaltyConfig };
                          newConfig[key].value = e.target.value;
                          setLoyaltyConfig(newConfig);
                        }}
                      />
                      <Button size="sm" onClick={() => handleUpdateConfig(key, config.value)}>
                        Guardar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* ─── Dialog: Codigos Generados (Template) ─────────────────── */}
      <Dialog open={!!viewingCampaignCodes} onOpenChange={(open) => { if (!open) setViewingCampaignCodes(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Codigos — {viewingCampaignCodes?.name}</DialogTitle>
          </DialogHeader>
          {campaignCodes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No hay codigos generados aun</p>
              <p className="text-xs text-muted-foreground mt-1">Se generan cuando un sistema externo (ManyChat) solicita un codigo</p>
            </div>
          ) : (
            <>
              <div className="relative w-full overflow-x-auto max-h-[50vh]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Usuario</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Codigo</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Creado</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Usos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaignCodes.map((item) => {
                      const statusBadge: Record<string, { label: string; className: string }> = {
                        active: { label: 'Activo', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
                        used: { label: 'Usado', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
                        expired: { label: 'Vencido', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
                        disabled: { label: 'Desactivado', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' }
                      };
                      const badge = statusBadge[item.status] || statusBadge.active;
                      return (
                        <TableRow key={item.id}>
                          <TableCell className="py-1 px-2 align-middle">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {(item.ig_name || item.ig_username || '?')[0].toUpperCase()}
                              </div>
                              <div>
                                {item.ig_name && <div className="font-medium text-xs">{item.ig_name}</div>}
                                <div className="text-xs text-violet-500">@{item.ig_username}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle"><span className="font-mono font-bold tracking-wider">{item.code}</span></TableCell>
                          <TableCell className="py-1 px-2 align-middle"><Badge className={badge.className}>{badge.label}</Badge></TableCell>
                          <TableCell className="text-muted-foreground">{new Date(item.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</TableCell>
                          <TableCell className="text-muted-foreground">{item.current_uses || 0}/{item.max_uses || 1}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {campaignCodesPagination.pages > 1 && (
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-sm text-muted-foreground">{campaignCodesPagination.total} codigos</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={campaignCodesPagination.page <= 1} onClick={() => viewingCampaignCodes && fetchCampaignCodes(viewingCampaignCodes.id, campaignCodesPagination.page - 1)}>
                      Anterior
                    </Button>
                    <span className="px-3 py-1 text-sm text-muted-foreground">{campaignCodesPagination.page} / {campaignCodesPagination.pages}</span>
                    <Button variant="outline" size="sm" disabled={campaignCodesPagination.page >= campaignCodesPagination.pages} onClick={() => viewingCampaignCodes && fetchCampaignCodes(viewingCampaignCodes.id, campaignCodesPagination.page + 1)}>
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Dialog: Agregar Premio ──────────────────────────────── */}
      <Dialog open={showAddReward} onOpenChange={setShowAddReward}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo premio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={rewardForm.name} onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })} placeholder="Ej: 10% de descuento" />
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Input value={rewardForm.description} onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })} placeholder="Descripcion del premio" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Puntos requeridos</Label>
                <Input type="number" min="0" value={rewardForm.pointsRequired} onChange={(e) => setRewardForm({ ...rewardForm, pointsRequired: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={rewardForm.rewardType} onValueChange={(v) => setRewardForm({ ...rewardForm, rewardType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">Descuento</SelectItem>
                    <SelectItem value="product">Producto</SelectItem>
                    <SelectItem value="service">Servicio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Descuento (%)</Label>
                <Input type="number" min="0" max="100" value={rewardForm.discountPercentage} onChange={(e) => setRewardForm({ ...rewardForm, discountPercentage: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Descuento ($)</Label>
                <Input type="number" min="0" value={rewardForm.discountAmount} onChange={(e) => setRewardForm({ ...rewardForm, discountAmount: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddReward(false)}>Cancelar</Button>
            <Button onClick={handleAddReward}>Crear premio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog: Editar Premio ───────────────────────────────── */}
      <Dialog open={showEditReward} onOpenChange={setShowEditReward}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar premio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={rewardForm.name} onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Input value={rewardForm.description} onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Puntos requeridos</Label>
                <Input type="number" min="0" value={rewardForm.pointsRequired} onChange={(e) => setRewardForm({ ...rewardForm, pointsRequired: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Descuento (%)</Label>
                <Input type="number" min="0" max="100" value={rewardForm.discountPercentage} onChange={(e) => setRewardForm({ ...rewardForm, discountPercentage: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditReward(false)}>Cancelar</Button>
            <Button onClick={handleEditReward}>Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog: Canjear Puntos ──────────────────────────────── */}
      <Dialog open={showRedeem} onOpenChange={setShowRedeem}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Canjear puntos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Puntos disponibles: <strong>{clientPoints?.points || 0}</strong></p>
            <div className="space-y-2">
              <Label>Seleccionar premio</Label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {rewards.filter(r => r.isActive).map((reward) => (
                  <button
                    key={reward.id}
                    onClick={() => setSelectedReward(reward)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedReward?.id === reward.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                    }`}
                  >
                    <div className="font-medium text-sm">{reward.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{reward.pointsRequired} puntos</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeem(false)}>Cancelar</Button>
            <Button onClick={handleRedeemPoints} disabled={!selectedReward}>Canjear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Loyalty;
